import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Schedule as ScheduleIcon,
  AttachMoney as AttachMoneyIcon,
  Star as StarIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Message as MessageIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  blue,
  green,
  red,
  grey,
  orange,
  purple,
  teal,
  amber,
} from "@mui/material/colors";
import { formatDateDurationForJoinedDateExtended } from "../../utils/otherDateFormatter";

const RoomDetailModal = ({ open, room, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return formatDateDurationForJoinedDateExtended(dateString, true);
    } catch {
      return "Invalid date";
    }
  };

  const getRoomTypeIcon = (type) => {
    return type === "group" ? (
      <GroupIcon fontSize="small" />
    ) : (
      <MessageIcon fontSize="small" />
    );
  };

  const getRoomStatusIcon = (status) => {
    return status === "public" ? (
      <PublicIcon fontSize="small" />
    ) : (
      <LockIcon fontSize="small" />
    );
  };

  const getParticipantRoleIcon = (participant) => {
    if (participant.is_admin)
      return <AdminPanelSettingsIcon fontSize="small" />;
    return <PersonIcon fontSize="small" />;
  };

  const getRoomTypeLabel = (type) => {
    return type === "group" ? "Group Chat" : "Direct Message";
  };

  const getSettingsCards = (room) => {
    const settings = [];

    if (room.join_fee && room.join_fee > 0) {
      settings.push({
        title: "Join Fee",
        value: `${room.join_fee} coins`,
        icon: <AttachMoneyIcon fontSize="small" />,
        color: green[500],
      });
    }

    if (room.watch_fee && room.watch_fee > 0) {
      settings.push({
        title: "Watch Fee",
        value: `${room.watch_fee} coins`,
        icon: <VisibilityIcon fontSize="small" />,
        color: blue[500],
      });
    }

    if (room.user_xp_level && room.user_xp_level > 0) {
      settings.push({
        title: "Required XP Level",
        value: `${room.user_xp_level} XP`,
        icon: <StarIcon fontSize="small" />,
        color: amber[600],
      });
    }

    if (room.duration_in_mins) {
      settings.push({
        title: "Duration",
        value: `${room.duration_in_mins} minutes`,
        icon: <ScheduleIcon fontSize="small" />,
        color: purple[500],
      });
    }

    if (room.total_credits && room.total_credits > 0) {
      settings.push({
        title: "Total Credits",
        value: `${room.total_credits} coins`,
        icon: <AttachMoneyIcon fontSize="small" />,
        color: teal[500],
      });
    }

    return settings;
  };

  if (!room) return null;

  const settings = getSettingsCards(room);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, px: 3, pt: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Avatar
              sx={{
                bgcolor: room.type === "group" ? blue[500] : green[500],
                width: 52,
                height: 52,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                border: "3px solid white",
              }}
            >
              {getRoomTypeIcon(room.type)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  background: "linear-gradient(45deg, #1976d2, #42a5f5)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {room.name || `Room ${room.id?.slice(-8)}`}
              </Typography>

              {/* Enhanced ID Badge */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: grey[100],
                  borderRadius: 2,
                  border: `1px solid ${grey[200]}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    backgroundColor: blue[500],
                    borderRadius: "50%",
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%": {
                        opacity: 1,
                      },
                      "50%": {
                        opacity: 0.5,
                      },
                      "100%": {
                        opacity: 1,
                      },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: grey[600],
                    fontWeight: 500,
                    fontSize: "0.7rem",
                    fontFamily: "monospace",
                    letterSpacing: 0.5,
                  }}
                >
                  ID: {room.id}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Tooltip title="Close">
            <IconButton
              onClick={onClose}
              sx={{
                bgcolor: grey[50],
                border: `1px solid ${grey[200]}`,
                "&:hover": {
                  bgcolor: grey[100],
                  transform: "scale(1.05)",
                  transition: "all 0.2s ease",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Grid container spacing={3}>
          {/* Room Details Section */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2.5,
                height: "100%",
                borderRadius: 3,
                border: `1px solid ${grey[200]}`,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 2.5,
                  fontSize: "1.1rem",
                  color: grey[800],
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 20,
                    backgroundColor: blue[500],
                    borderRadius: 2,
                  }}
                />
                Room Details
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Type and Visibility side by side */}
                <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                    >
                      Type
                    </Typography>
                    <Chip
                      icon={getRoomTypeIcon(room.type)}
                      label={getRoomTypeLabel(room.type)}
                      color={room.type === "group" ? "primary" : "success"}
                      size="small"
                      sx={{
                        fontWeight: 400,
                        fontSize: "0.75rem",
                        height: 24,
                        "& .MuiChip-icon": { fontSize: "0.9rem" },
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                    >
                      Visibility
                    </Typography>
                    <Chip
                      icon={getRoomStatusIcon(room.status)}
                      label={room.status === "public" ? "Public" : "Private"}
                      color={room.status === "public" ? "success" : "warning"}
                      variant="outlined"
                      size="small"
                      sx={{
                        fontWeight: 400,
                        fontSize: "0.75rem",
                        height: 24,
                        "& .MuiChip-icon": { fontSize: "0.9rem" },
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                {/* Created and Expires side by side */}
                <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                    >
                      Created
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        fontSize: "0.8rem",
                        color: "text.primary",
                        lineHeight: 1.3,
                      }}
                    >
                      {formatDate(room.created_at)}
                    </Typography>
                  </Box>

                  {room.expiration_time ? (
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                      >
                        Expires
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 400,
                          fontSize: "0.8rem",
                          color: "text.primary",
                          lineHeight: 1.3,
                        }}
                      >
                        {formatDate(room.expiration_time)}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                      >
                        Duration
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 400,
                          fontSize: "0.8rem",
                          color: "text.secondary",
                          fontStyle: "italic",
                        }}
                      >
                        Permanent
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 0.5 }} />

                {/* Status at the bottom */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    Status
                  </Typography>
                  <Chip
                    label={
                      room.is_expired
                        ? "Expired"
                        : room.is_active
                        ? "Active"
                        : "Inactive"
                    }
                    color={
                      room.is_expired
                        ? "error"
                        : room.is_active
                        ? "success"
                        : "default"
                    }
                    size="small"
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.75rem",
                      height: 24,
                      boxShadow: room.is_active
                        ? "0 2px 4px rgba(76, 175, 80, 0.2)"
                        : room.is_expired
                        ? "0 2px 4px rgba(244, 67, 54, 0.2)"
                        : "none",
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Settings Section */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2.5,
                height: "100%",
                borderRadius: 3,
                border: `1px solid ${grey[200]}`,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 2.5,
                  fontSize: "1.1rem",
                  color: grey[800],
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 20,
                    backgroundColor: green[500],
                    borderRadius: 2,
                  }}
                />
                Settings
              </Typography>

              {settings.length > 0 ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {settings.map((setting, index) => (
                    <React.Fragment key={index}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 2,
                          backgroundColor: "rgba(255,255,255,0.7)",
                          borderRadius: 2.5,
                          border: `1px solid ${grey[100]}`,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: setting.color,
                            width: 34,
                            height: 34,
                            mr: 2,
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                          }}
                        >
                          {React.cloneElement(setting.icon, {
                            sx: { fontSize: "1rem" },
                          })}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.7rem",
                              mb: 0.3,
                              fontWeight: 600,
                            }}
                          >
                            {setting.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.9rem",
                              color: grey[800],
                            }}
                          >
                            {setting.value}
                          </Typography>
                        </Box>
                      </Box>
                      {index < settings.length - 1 && (
                        <Divider sx={{ mx: 0, my: 0.2 }} />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 4,
                    color: "text.secondary",
                  }}
                >
                  <ScheduleIcon sx={{ fontSize: 40, mb: 1.5, opacity: 0.5 }} />
                  <Typography
                    variant="body2"
                    sx={{ textAlign: "center", fontSize: "0.85rem" }}
                  >
                    No special settings configured
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ textAlign: "center", mt: 0.3, fontSize: "0.75rem" }}
                  >
                    This room uses default settings
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Participants Section */}
          {room.participants && room.participants.length > 0 && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: `1px solid ${grey[200]}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    fontSize: "1.1rem",
                    color: grey[800],
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 20,
                      backgroundColor: purple[500],
                      borderRadius: 2,
                    }}
                  />
                  Participants ({room.participants.length})
                </Typography>
                <List sx={{ pt: 0 }}>
                  {room.participants.slice(0, 5).map((participant, index) => (
                    <React.Fragment key={participant.user_id || participant.id}>
                      <ListItem
                        sx={{
                          px: 0,
                          py: 1,
                          borderRadius: 2,
                          "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: participant.is_admin
                                ? blue[500]
                                : participant.is_banned
                                ? red[500]
                                : green[500],
                              width: 38,
                              height: 38,
                              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                            }}
                          >
                            {getParticipantRoleIcon(participant)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 400, fontSize: "0.85rem" }}
                              >
                                {(() => {
                                  // Clean the username by removing trailing "000" and whitespace
                                  const cleanName = (
                                    participant.username ||
                                    participant.first_name ||
                                    participant.name ||
                                    "Unknown User"
                                  )
                                    .replace(/\s*000\s*$/, "")
                                    .trim();

                                  return cleanName || "Unknown User";
                                })()}
                              </Typography>
                              {participant.is_admin ? (
                                <Chip
                                  label="Admin"
                                  size="small"
                                  color="primary"
                                  sx={{
                                    fontSize: "0.65rem",
                                    height: 18,
                                    fontWeight: 500,
                                  }}
                                />
                              ) : null}
                              {participant.is_banned ? (
                                <Chip
                                  label="Banned"
                                  size="small"
                                  color="error"
                                  sx={{
                                    fontSize: "0.65rem",
                                    height: 18,
                                    fontWeight: 500,
                                  }}
                                />
                              ) : null}
                              {participant.is_verified ? (
                                <Chip
                                  label="Verified"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{
                                    fontSize: "0.65rem",
                                    height: 18,
                                    fontWeight: 500,
                                  }}
                                />
                              ) : null}
                              {participant.is_moderator ? (
                                <Chip
                                  label="Moderator"
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{
                                    fontSize: "0.65rem",
                                    height: 18,
                                    fontWeight: 500,
                                  }}
                                />
                              ) : null}
                            </Box>
                          }
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "0.7rem" }}
                              >
                                {participant.email}
                              </Typography>
                              {participant.joined_at && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: "0.65rem",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Joined:{" "}
                                  {(() => {
                                    try {
                                      return new Date(
                                        participant.joined_at
                                      ).toLocaleDateString();
                                    } catch {
                                      return "Unknown";
                                    }
                                  })()}
                                </Typography>
                              )}
                              {participant.role &&
                                participant.role !== "member" && (
                                  <Typography
                                    variant="caption"
                                    color="primary.main"
                                    sx={{
                                      fontSize: "0.65rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Role:{" "}
                                    {participant.role.charAt(0).toUpperCase() +
                                      participant.role.slice(1)}
                                  </Typography>
                                )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < Math.min(room.participants.length, 5) - 1 && (
                        <Divider sx={{ mx: 0 }} />
                      )}
                    </React.Fragment>
                  ))}
                  {room.participants.length > 5 && (
                    <>
                      <Divider sx={{ mx: 0 }} />
                      <ListItem sx={{ px: 0, py: 0.5 }}>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: "italic", fontSize: "0.8rem" }}
                            >
                              ... and {room.participants.length - 5} more
                              participants
                            </Typography>
                          }
                        />
                      </ListItem>
                    </>
                  )}
                </List>
              </Paper>
            </Grid>
          )}

          {/* Tags Section */}
          {room.tags && room.tags.trim() && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: `1px solid ${grey[200]}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    fontSize: "1.1rem",
                    color: grey[800],
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 20,
                      backgroundColor: orange[500],
                      borderRadius: 2,
                    }}
                  />
                  Tags
                </Typography>
                <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
                  {room.tags
                    .split(",")
                    .filter((tag) => tag.trim())
                    .map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag.trim()}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          fontWeight: 500,
                          fontSize: "0.7rem",
                          height: 24,
                          border: `1px solid ${grey[300]}`,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: grey[100],
                            transform: "translateY(-1px)",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          },
                        }}
                      />
                    ))}
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            minWidth: 120,
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            background: "linear-gradient(45deg, #1976d2, #42a5f5)",
            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
            "&:hover": {
              background: "linear-gradient(45deg, #1565c0, #1976d2)",
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoomDetailModal;
